import React, { FC, useCallback, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import cityTimezones, { CityData } from "city-timezones";
import { Form as BaseForm, Formik, FormikHelpers } from "formik";
import styled from "styled-components";

import { API } from "@api";
import { IRequestTopic } from "@api/utils";
import {
  Button,
  Disclaimer,
  FileField,
  Icon,
  Loader,
  Modal,
  NumberField,
  SelectField,
  SwitchField,
  Tabs,
  TextAreaField,
  TextField,
} from "@components";
import { ButtonGroupField } from "@components/ButtonGroupField";
import { DateField } from "@components/DateField";
import DepositAttachmentField from "@components/DepositAttachmentField";
import { useGlobalContext } from "@contexts/GlobalContext";
import { useI18n } from "@i18n";
import { device } from "@styles/breakpoints";
import {
  Agency,
  auctionTime,
  getCityName,
  getUTCDisclaimerSections,
  ICity,
  ICountry,
  IEmailLanguage,
} from "@utils";
import { Label } from "../../Label";
import { AuctioneerFeeByAssetField } from "./AuctioneerFeeByAssetField";
import {
  MAX_CONVERSION_RATE_VALUE,
  MIN_CONVERSION_RATE_VALUE,
  MIN_NON_WFP_LOTS_VALUE,
  MIN_UNHCR_SERVICES_VALUE,
} from "./consts";
import { DepositByAssetField } from "./DepositByAssetField";
import { EmailField } from "./EmailField";
import { INITIAL_VALUES } from "./initialValues";
import { Stakeholders } from "./Stakeholders";
import { validateForm, VALIDATION_SCHEMA } from "./validationSchema";

export type TCreateAuctionFormValues = Omit<
  typeof INITIAL_VALUES,
  "stakeholders"
> & {
  stakeholders?: typeof INITIAL_VALUES["stakeholders"];
};

type Props = {
  loading?: boolean;
  onSubmit: (
    values: TCreateAuctionFormValues,
    helpers: FormikHelpers<TCreateAuctionFormValues>
  ) => Promise<void>;
};

const Form = styled(BaseForm)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SwitchContainer = styled.div`
  align-items: center;
  display: flex;
  text-transform: uppercase;
  justify-content: space-between;

  span {
    min-width: 280px;
  }

  div {
    margin-top: 0;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.tripleMargin};

  & > *:not(:last-child) {
    margin-right: ${({ theme }) => theme.tripleMargin};
  }

  @media ${device.tablet} {
    flex-direction: column;

    & > *:not(:last-child) {
      margin-right: 0;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: 300px;

  img {
    max-width: 300px;
  }
`;

const ImageDeleteButton = styled.div`
  position: absolute;
  right: -5px;
  top: -10px;
  cursor: pointer;
`;

const RowWithLabel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LabelBox = styled.div`
  display: flex;
  align-items: center;
  padding-right: 15px;
  width: 200px;

  & ${Label} {
    margin-right: auto;
  }
`;

const LabelBoxPaddedBottom = styled(LabelBox)`
  padding-bottom: 6px;
`;

const LabelSpan = styled.span`
  text-transform: initial;
`;

const TooltipContent = styled.div`
  max-width: 20rem;
`;

/** Extracted from inline styles (minimal new components) */
const FlexAlignCenterPR20 = styled.div`
  display: flex;
  align-items: center;
  padding-right: 20px;
`;

const FlexAlignCenterPR20PL26 = styled(FlexAlignCenterPR20)`
  padding-left: 26px;
`;

const AlignEndBox = styled.div`
  display: flex;
  align-items: flex-end;
`;

const DesktopLayout = styled.div`
  display: flex;

  @media (max-width: 960px) {
    display: none;
  }
`;

const MobileLayout = styled.div`
  display: none;

  @media (max-width: 960px) {
    display: block;
  }
`;

const TopicNameLabel = styled(Label)<{ bold?: boolean }>`
  font-size: 14px;
  text-transform: initial;
  font-weight: ${({ bold }) => (bold ? "bold" : "inherit")};
`;

const MobileSwitchContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const CustomEmailsContainer = styled.div`
  margin-bottom: 24px;
`;

const CustomEmailsLabel = styled(Label)`
  font-size: 12px;
  text-transform: initial;
`;

const EmailFieldWrapper = styled.div`
  margin-top: 16px;
`;

const DesktopSwitchContainer = styled.div`
  width: 20%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const DesktopColumnHeader = styled.div`
  width: 20%;
  text-align: center;
`;

const DesktopEmailColumnHeader = styled.div`
  width: 40%;
  text-align: left;
`;

const DesktopTopicColumn = styled.div`
  width: 20%;
`;

const DesktopEmailColumn = styled.div`
  width: 40%;
`;

const TopicContainer = styled.div`
  margin-bottom: 16px;
`;

// Reusable Switch Field Component
interface MobileSwitchFieldProps {
  topicId: number;
  label: string;
  fieldName: string;
  setFieldValue: (field: string, value: any) => void;
}

const MobileSwitchField: React.FC<MobileSwitchFieldProps> = ({
  topicId,
  label,
  fieldName,
  setFieldValue,
}) => (
  <MobileSwitchContainer>
    <SwitchField
      label={label}
      labelStyles={{ fontSize: "12px", textTransform: "initial" }}
      horizontal={true}
      name={`requestTopics.${topicId}.${fieldName}`}
      onChange={(checked) => {
        setFieldValue(`requestTopics.${topicId}.topic`, topicId);
        setFieldValue(`requestTopics.${topicId}.${fieldName}`, checked);
      }}
    />
  </MobileSwitchContainer>
);

export const CreateAuctionForm: FC<Props> = ({ loading, onSubmit }) => {
  const {
    waffles: {
      switches: { stakeholder },
    },
  } = useGlobalContext();

  const { t } = useI18n();
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [cities, setCities] = useState<ICity[]>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [currencies, setCurrencies] = useState<{ code: string }[]>([]);
  const [emailLanguages, setEmailLanguages] = useState<IEmailLanguage[]>([]);
  const [conversionRateDisabled, setConversionRateDisabled] = useState(false);
  const [auctionImage, setAuctionImage] = useState<string>("");
  const [uploadedPhoto, setUploadedPhoto] = useState<any>("");
  const [timezones, setTimezones] = useState<Array<any>>([]);
  const [gasStrategies, setGasStrategies] = useState<Array<any>>([]);
  const [requestTopics, setRequestTopics] = useState<IRequestTopic[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    const getCountries = async () => {
      try {
        const countries = await API.users.countries();
        setCountries(countries);
      } catch (_) {
        setCountries([]);
      }
    };

    const getCurrencies = async () => {
      try {
        const currencies = await API.utils.currencies();
        setCurrencies(currencies);
      } catch (_) {
        setCurrencies([]);
      }
    };

    const getEmailLanguages = async () => {
      try {
        const languages = await API.auctions.getEmailLanguages();
        setEmailLanguages(languages);
      } catch (_) {
        setEmailLanguages([]);
      }
    };

    const getTimezones = async () => {
      try {
        const timezones = await API.utils.getTimezones();
        setTimezones(timezones);
      } catch (_) {
        setTimezones([]);
      }
    };

    const getGasStrategies = async () => {
      try {
        const strategies = await API.utils.getGasStrategies();
        setGasStrategies(strategies.results);
      } catch (_) {
        setGasStrategies([]);
      }
    };

    const getRequestTopics = async () => {
      try {
        const topics = await API.utils.getRequestTopics();
        setRequestTopics(topics.results);
      } catch (_) {
        setRequestTopics([]);
      }
    };

    (async () => {
      await Promise.all([
        getCountries(),
        getCurrencies(),
        getEmailLanguages(),
        getTimezones(),
        getGasStrategies(),
        getRequestTopics(),
      ]);
      setInitialLoading(false);
    })();
  }, []);

  useEffect(() => {
    createUrl(uploadedPhoto);
  }, [uploadedPhoto]);

  const createUrl = (files: any): void => {
    let url: any = [];
    for (let file in files) {
      typeof files[file] === "object" &&
        url.push(URL.createObjectURL(files[file]));
    }
    setAuctionImage(url[0]);
  };

  const handlePhotoField = (photo: any, setFieldValue: any) => {
    setFieldValue("auctionImage", photo[0]);
    setUploadedPhoto(photo);
  };

  const getCitiesByCountry = async (countryId: number) => {
    try {
      const cities = await API.users.citiesByCountry(countryId);
      setCities(cities);
    } catch (_) {
      setCities([]);
    }
  };

  const transformRequestTopicsData = (values: any) => {
    if (!requestTopics || !requestTopics.length) return [];

    return requestTopics.map((topic) => {
      const topicData = values.requestTopics?.[topic.id];
      return {
        topic: topic.id,
        bcc_gas_representative: topicData?.bcc_gas_representative ?? true,
        bcc_co_focal_point: topicData?.bcc_co_focal_point ?? true,
        additional_emails: topicData?.additional_emails || [],
      };
    });
  };

  const handleSubmit = useCallback(
    async (values, helpers) => {
      helpers.setSubmitting(true);

      // Transform request topics data
      const transformedValues = {
        ...values,
        requestTopics: transformRequestTopicsData(values),
      };

      await onSubmit(transformedValues, helpers);
      helpers.setSubmitting(false);
    },
    [onSubmit, requestTopics]
  );

  const handleCountryChange = async (countryId: number) => {
    await getCitiesByCountry(countryId);
  };

  return (
    <Formik<TCreateAuctionFormValues>
      initialValues={INITIAL_VALUES}
      validate={validateForm}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isValid,
        touched,
        values,
        handleSubmit,
        handleChange: onChange,
        setValues,
        setFieldValue,
        resetForm,
        errors,
      }) => {
        // Initialize request topics form values when requestTopics are loaded or when isOnline changes
        useEffect(() => {
          if (requestTopics && requestTopics.length > 0 && values.isOnline) {
            const initialTopicValues: Record<string, any> =
              requestTopics.reduce((acc, topic) => {
                acc[`requestTopics.${topic.id}.topic`] = topic.id;
                acc[`requestTopics.${topic.id}.bcc_gas_representative`] = true;
                acc[`requestTopics.${topic.id}.bcc_co_focal_point`] = true;
                acc[`requestTopics.${topic.id}.additional_emails`] = [];
                return acc;
              }, {} as Record<string, any>);

            // Set initial values for request topics
            Object.keys(initialTopicValues).forEach((key) => {
              setFieldValue(key, initialTopicValues[key]);
            });
          } else if (!values.isOnline) {
            // Clear request topics values when switching to offline
            if (requestTopics && requestTopics.length > 0) {
              requestTopics.forEach((topic) => {
                setFieldValue(`requestTopics.${topic.id}.topic`, undefined);
                setFieldValue(
                  `requestTopics.${topic.id}.bcc_gas_representative`,
                  undefined
                );
                setFieldValue(
                  `requestTopics.${topic.id}.bcc_co_focal_point`,
                  undefined
                );
                setFieldValue(
                  `requestTopics.${topic.id}.additional_emails`,
                  undefined
                );
              });
            }
          }
        }, [requestTopics, setFieldValue, values.isOnline]);

        // Custom validation for custom deposit requirements
        const isCustomDepositValid =
          !values.customDeposit ||
          (values.customDepositName &&
            values.customDepositName.trim() &&
            values.depositAttachment);

        const isFormValid = isValid && isCustomDepositValid;
        const disclaimerSections = getUTCDisclaimerSections(values);

        return (
          <Form onSubmit={handleSubmit}>
            <Modal
              width="100%"
              maxWidth="1400px"
              title={t("pages.createAuction.title")}
              primaryActions={<></>}
              actions={
                <>
                  <Button
                    bordered
                    onClick={() => {
                      setValues(INITIAL_VALUES);
                      setAuctionImage("");
                      setUploadedPhoto("");

                      // Reset request topics form values
                      if (requestTopics && requestTopics.length > 0) {
                        requestTopics.forEach((topic) => {
                          setFieldValue(
                            `requestTopics.${topic.id}.topic`,
                            topic.id
                          );
                          setFieldValue(
                            `requestTopics.${topic.id}.bcc_gas_representative`,
                            true
                          );
                          setFieldValue(
                            `requestTopics.${topic.id}.bcc_co_focal_point`,
                            true
                          );
                          setFieldValue(
                            `requestTopics.${topic.id}.additional_emails`,
                            []
                          );
                        });
                      }
                    }}
                  >
                    {t<string>("auctionForm.clear")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!Object.values(touched).length || !isFormValid}
                    onClick={() => null}
                    loading={loading}
                  >
                    {t<string>("auctionForm.submit")}
                  </Button>
                </>
              }
              onClose={() => null}
              fixed
            >
              {initialLoading && <Loader size="big" full />}
              <Row>
                <SwitchContainer>
                  <span>{t("auctionForm.holdOnline")}</span>
                  <SwitchField name="isOnline" resetForm={resetForm} />
                </SwitchContainer>
              </Row>
              <Label>{t("auctionForm.location")}</Label>
              <Row>
                <SelectField
                  name="country"
                  placeholder={t("auctionForm.country")}
                  options={countries.map((country) => ({
                    label: country.name,
                    value: country.geoname_id,
                  }))}
                  onChange={(event) => {
                    setFieldValue("city", "");
                    onChange(event);
                    handleCountryChange(Number(event.target.value));
                  }}
                />
                <SelectField
                  name="city"
                  placeholder={t("auctionForm.city")}
                  options={cities.map((city) => ({
                    label: getCityName(city),
                    value: city.geoname_id,
                  }))}
                  disabled={!Boolean(values.country) || cities.length === 0}
                  onChange={(event) => {
                    onChange(event);
                    const city = cities.find(
                      (city) => city.geoname_id === +event.target.value
                    )?.name_ascii;
                    if (!city) return;
                    let cityLookup: CityData | undefined;
                    cityLookup = cityTimezones.lookupViaCity(city)[0];
                    if (!cityLookup) {
                      const country = countries.find(
                        (i): boolean => i.geoname_id === +values.country
                      )?.code2;
                      cityLookup = cityTimezones.cityMapping.find(
                        (cityData): boolean => cityData.iso2 === country
                      );
                    }
                    if (!cityLookup) return;
                    setFieldValue("timezone", cityLookup.timezone);
                  }}
                />
              </Row>

              <Row>
                <DateField
                  name="date"
                  placeholder={t("auctionForm.date.placeholder")}
                  label={t("auctionForm.date.label")}
                />
                {!values.isOnline ? (
                  <SelectField
                    name="currency"
                    placeholder={t("auctionForm.currency.placeholder")}
                    label={t("auctionForm.currency.label")}
                    options={currencies.map((currency) => ({
                      label: currency.code,
                      value: currency.code,
                    }))}
                    onChange={(event) => {
                      onChange(event);
                      const currency = event.target.value;
                      if (currency === "USD") {
                        setConversionRateDisabled(true);
                        setFieldValue("conversionRate", 1);
                      } else {
                        setConversionRateDisabled(false);
                        setFieldValue("conversionRate", "");
                      }
                    }}
                  />
                ) : (
                  <>
                    <SelectField
                      name="auctionTime"
                      placeholder={String(
                        t("auctionForm.auctionTime.placeholder")
                      )}
                      label={String(t("auctionForm.auctionTime.label"))}
                      options={auctionTime}
                    />
                  </>
                )}
              </Row>

              {values.isOnline && (
                <>
                  <Row>
                    <DateField
                      name="auctionEndDate"
                      placeholder={t("auctionForm.auctionEndDate.placeholder")}
                      label={t("auctionForm.auctionEndDate.label")}
                    />
                    <SelectField
                      name="auctionEndTime"
                      label={String(t("auctionForm.auctionEndTime.label"))}
                      placeholder={String(
                        t("auctionForm.auctionEndTime.placeholder")
                      )}
                      options={auctionTime}
                    />
                  </Row>
                  {stakeholder ? <Stakeholders countries={countries} /> : null}
                  <Row>
                    <SelectField
                      name="timezone"
                      placeholder={String(
                        t("auctionForm.timezone.placeholder")
                      )}
                      label={String(t("auctionForm.timezone.label"))}
                      options={timezones.map((zone) => ({
                        label: zone.display_name,
                        value: zone.value,
                      }))}
                    />
                  </Row>
                  {disclaimerSections.length > 0 && (
                    <Row>
                      <Disclaimer>
                        <span>
                          The current setting corresponds to the following UTC
                          date and time.
                          <br />
                          {disclaimerSections.join(" - ")}
                        </span>
                      </Disclaimer>
                    </Row>
                  )}
                  <Row>
                    <SelectField
                      name="currency"
                      placeholder={t("auctionForm.currency.placeholder")}
                      label={t("auctionForm.currency.label")}
                      options={currencies.map((currency) => ({
                        label: currency.code,
                        value: currency.code,
                      }))}
                      onChange={(event) => {
                        onChange(event);
                        const currency = event.target.value;
                        if (currency === "USD") {
                          setConversionRateDisabled(true);
                          setFieldValue("conversionRate", 1);
                        } else {
                          setConversionRateDisabled(false);
                          setFieldValue("conversionRate", "");
                        }
                      }}
                    />
                  </Row>
                </>
              )}

              {!values.isOnline && stakeholder ? (
                <Stakeholders countries={countries} />
              ) : null}
              <Row>
                <NumberField
                  name="conversionRate"
                  label={t("auctionForm.conversionRate.label")}
                  placeholder={t("auctionForm.conversionRate.placeholder")}
                  min={MIN_CONVERSION_RATE_VALUE}
                  max={MAX_CONVERSION_RATE_VALUE}
                  step="0.001"
                  disabled={conversionRateDisabled}
                />
              </Row>
              {values.isOnline && (
                <>
                  <Row>
                    <SwitchContainer>
                      <FlexAlignCenterPR20>
                        <Label className="mr-6">
                          {t("auctionForm.depositToggle")}
                        </Label>
                      </FlexAlignCenterPR20>
                      <AlignEndBox>
                        <SwitchField
                          name="requiresOnlineDeposits"
                          disabled={!!values.deposits.length}
                        />
                      </AlignEndBox>
                    </SwitchContainer>
                  </Row>
                  {values.requiresOnlineDeposits && (
                    <>
                      <DepositByAssetField />

                      <Row>
                        <SwitchContainer>
                          <FlexAlignCenterPR20>
                            <Label className="mr-6">
                              {t("auctionForm.enableDepositBankTransfer")}
                            </Label>
                          </FlexAlignCenterPR20>
                          <AlignEndBox>
                            <SwitchField name="enableDepositBankTransfer" />
                          </AlignEndBox>

                          <FlexAlignCenterPR20PL26>
                            <Label className="mr-6">
                              {t("auctionForm.enableDepositOnlinePayment")}
                            </Label>
                          </FlexAlignCenterPR20PL26>
                          <AlignEndBox>
                            <SwitchField name="enableDepositOnlinePayment" />
                          </AlignEndBox>

                          <FlexAlignCenterPR20PL26>
                            <Label className="mr-6">
                              {t("auctionForm.custom")}
                            </Label>
                          </FlexAlignCenterPR20PL26>
                          <AlignEndBox>
                            <SwitchField name="customDeposit" />
                          </AlignEndBox>
                        </SwitchContainer>
                      </Row>
                      {values.customDeposit && (
                        <>
                          <div style={{ marginBottom: "12px" }}>
                            <Label>
                              {t("auctionForm.customDepositName")}
                              {" * "}
                              <LabelSpan>
                                {t("auctionForm.customDepositNote")}
                              </LabelSpan>
                            </Label>
                            <TextField
                              name="customDepositName"
                              placeholder={t("auctionForm.customDepositName")}
                            />
                          </div>

                          <DepositAttachmentField
                            name="depositAttachment"
                            disabled={!values.requiresOnlineDeposits}
                            required
                          />
                        </>
                      )}
                      <Row>
                        <DateField
                          name="depositDate"
                          disabled={Object.keys(values.deposits).length === 0}
                          placeholder={t("auctionForm.depositDate.placeholder")}
                          label={t("auctionForm.depositDate.label")}
                        />
                        <SelectField
                          name="depositTime"
                          disabled={
                            Object.keys(values.deposits).length === 0 ||
                            !values.depositDate
                          }
                          label={String(t("auctionForm.depositTime.label"))}
                          placeholder={String(
                            t("auctionForm.depositTime.placeholder")
                          )}
                          options={auctionTime}
                        />
                      </Row>
                    </>
                  )}
                  <Label>{t("auctionForm.gasStrategy")}</Label>
                  <Row>
                    <SelectField
                      name="gasStrategy"
                      placeholder="Default"
                      options={gasStrategies.map((strategy) => ({
                        label: strategy.name,
                        value: strategy.id,
                      }))}
                    />
                  </Row>
                  <Row>
                    <SwitchContainer>
                      <FlexAlignCenterPR20>
                        <Label className="mr-6">
                          {t("auctionForm.emailNotification.outbid")}
                        </Label>
                        <div id="outbid-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </FlexAlignCenterPR20>
                      <AlignEndBox>
                        <SwitchField name="sendEmailBidWasOutbid" />
                      </AlignEndBox>
                    </SwitchContainer>
                    <Tooltip anchorId="outbid-tooltip">
                      <TooltipContent>
                        {t("auctionForm.emailNotificationTooltip.outbid")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>
                  <Row>
                    <RowWithLabel>
                      <Label>{t("auctionForm.emailLanguage")}</Label>
                      <SelectField
                        name="emailLanguage"
                        placeholder={t("auctionForm.emailLanguagePlaceholder")}
                        options={emailLanguages.map((language) => ({
                          label: language.name,
                          value: language.code,
                        }))}
                      />
                    </RowWithLabel>
                  </Row>
                  <Row>
                    <SwitchContainer>
                      <FlexAlignCenterPR20>
                        <Label className="mr-6">
                          {t("auctionForm.enableBankTransfer")}
                        </Label>
                      </FlexAlignCenterPR20>
                      <AlignEndBox>
                        <SwitchField name="enableBankTransfer" />
                      </AlignEndBox>

                      <FlexAlignCenterPR20PL26>
                        <Label className="mr-6">
                          {t("auctionForm.enableOnlinePayment")}
                        </Label>
                      </FlexAlignCenterPR20PL26>
                      <AlignEndBox>
                        <SwitchField name="enableOnlinePayment" />
                      </AlignEndBox>
                    </SwitchContainer>
                  </Row>
                </>
              )}
              <Row>
                <RowWithLabel>
                  <LabelBox>
                    <Label className="mr-6">
                      {t("auctionForm.paymentAccount")}
                    </Label>
                    <div id="payment-account-tooltip">
                      <Icon.InfoCircle />
                    </div>
                  </LabelBox>
                  <TextAreaField
                    name="paymentAccount"
                    placeholder={t("auctionForm.paymentAccount")}
                    style={{ resize: "vertical" }}
                  />
                </RowWithLabel>
                <Tooltip anchorId="payment-account-tooltip">
                  <TooltipContent>
                    {t("auctionForm.paymentAccountTooltip")}
                  </TooltipContent>
                </Tooltip>
              </Row>
              <Row>
                <RowWithLabel>
                  <LabelBoxPaddedBottom>
                    <Label className="mr-6">
                      {t("auctionForm.paymentConditions")}
                    </Label>
                    <div id="payment-conditions-tooltip">
                      <Icon.InfoCircle />
                    </div>
                  </LabelBoxPaddedBottom>
                  <Tabs
                    onTabClick={(index) => {
                      setActiveTabIndex(index);
                    }}
                    headers={[
                      String(t("auctionForm.languages.english")),
                      String(t("auctionForm.languages.french")),
                      String(t("auctionForm.languages.portuguese")),
                      String(t("auctionForm.languages.spanish")),
                    ]}
                  >
                    <TextAreaField
                      name="paymentConditions"
                      placeholder={t("auctionForm.paymentConditions")}
                      style={{ resize: "vertical", minHeight: "100px" }}
                    />
                    <TextAreaField
                      name="paymentConditionsFr"
                      placeholder={t("auctionForm.paymentConditions")}
                      style={{ resize: "vertical", minHeight: "100px" }}
                    />
                    <TextAreaField
                      name="paymentConditionsPt"
                      placeholder={t("auctionForm.paymentConditions")}
                      style={{ resize: "vertical", minHeight: "100px" }}
                    />
                    <TextAreaField
                      name="paymentConditionsEs"
                      placeholder={t("auctionForm.paymentConditions")}
                      style={{ resize: "vertical", minHeight: "100px" }}
                    />
                  </Tabs>
                </RowWithLabel>
                <Tooltip anchorId="payment-conditions-tooltip">
                  <TooltipContent>
                    {t("auctionForm.paymentConditionsTooltip")}
                  </TooltipContent>
                </Tooltip>
              </Row>
              {values.isOnline && (
                <>
                  <Row>
                    <RowWithLabel>
                      <LabelBox>
                        <Label className="mr-6">
                          {t("auctionForm.pickupDetails")}
                        </Label>
                        <div id="pickup-details-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </LabelBox>
                      <TextAreaField
                        name="pickupDetails"
                        style={{ resize: "vertical" }}
                      />
                    </RowWithLabel>
                    <Tooltip anchorId="pickup-details-tooltip">
                      <TooltipContent>
                        {t("auctionForm.pickupDetailsTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>
                  <Row>
                    <RowWithLabel>
                      <LabelBox>
                        <Label className="mr-6">
                          {t("auctionForm.clearanceInstructions")}
                        </Label>
                        <div id="clearance-instructions-tooltip">
                          <Icon.InfoCircle />
                        </div>
                      </LabelBox>
                      <TextAreaField
                        name="clearanceInstructions"
                        style={{ resize: "vertical" }}
                      />
                    </RowWithLabel>
                    <Tooltip anchorId="clearance-instructions-tooltip">
                      <TooltipContent>
                        {t("auctionForm.clearanceInstructionsTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </Row>
                </>
              )}
              {!values.isOnline && (
                <>
                  <AuctioneerFeeByAssetField />
                  <DepositByAssetField />
                  <Label>{t("auctionForm.gasStrategy")}</Label>
                  <Row>
                    <SelectField
                      name="gasStrategy"
                      placeholder="Default"
                      options={gasStrategies.map((strategy) => ({
                        label: strategy.name,
                        value: strategy.id,
                      }))}
                    />
                  </Row>
                  <Row>
                    <ButtonGroupField
                      name="agency"
                      label={t("auctionForm.agency")}
                      options={[
                        {
                          label: String(t("auctionForm.agencies.unhcr")),
                          value: Agency.UNHCR,
                        },
                        {
                          label: String(t("auctionForm.agencies.wfp")),
                          value: Agency.WFP,
                        },
                      ]}
                      onChange={(agency: Agency) => {
                        if (agency === Agency.WFP) {
                          setFieldValue(
                            "supervisor",
                            INITIAL_VALUES.supervisor
                          );
                          setFieldValue(
                            "nonWfpLots",
                            INITIAL_VALUES.nonWfpLots
                          );
                          setFieldValue(
                            "unhcrServicesFee",
                            INITIAL_VALUES.unhcrServicesFee
                          );
                        }
                      }}
                    />
                    <SwitchField
                      name="sealedBid"
                      label={
                        values.sealedBid
                          ? t("auctionForm.sealedBid")
                          : t("auctionForm.public")
                      }
                    />
                  </Row>
                </>
              )}
              {values.agency === Agency.UNHCR && !values.isOnline && (
                <>
                  <Row>
                    <NumberField
                      name="nonWfpLots"
                      label={t("auctionForm.nonWfpLots.label")}
                      placeholder={t("auctionForm.nonWfpLots.placeholder")}
                      step={1}
                      min={MIN_NON_WFP_LOTS_VALUE}
                    />
                    <NumberField
                      name="unhcrServicesFee"
                      label={
                        values.currency
                          ? t("auctionForm.unhcrServicesFee.label", {
                              currency: "(" + values.currency + ")",
                            })
                          : t("auctionForm.unhcrServicesFee.label")
                      }
                      placeholder={
                        values.currency
                          ? t("auctionForm.unhcrServicesFee.placeholder", {
                              currency: "(" + values.currency + ")",
                            })
                          : t("auctionForm.unhcrServicesFee.placeholder")
                      }
                      step={0.01}
                      min={MIN_UNHCR_SERVICES_VALUE}
                    />
                  </Row>
                  <Label>{t("auctionForm.supervisor")}</Label>
                  <Row>
                    <TextField
                      name="supervisor.fullName"
                      placeholder={t("auctionForm.fullName")}
                    />
                    <TextField
                      name="supervisor.email"
                      placeholder={t("auctionForm.email")}
                    />
                  </Row>
                  <Row>
                    <TextField
                      name="supervisor.phone"
                      placeholder={t("auctionForm.phone")}
                    />
                  </Row>
                </>
              )}

              {values.agency === Agency.WFP && !values.isOnline && (
                <Row>
                  <NumberField
                    name="auctioneerInvoice"
                    label={
                      values.currency
                        ? t("auctionForm.auctioneerInvoice.label", {
                            currency: "(" + values.currency + ")",
                          })
                        : t("auctionForm.auctioneerInvoice.label")
                    }
                    placeholder={
                      values.currency
                        ? t("auctionForm.auctioneerInvoice.label", {
                            currency: "(" + values.currency + ")",
                          })
                        : t("auctionForm.auctioneerInvoice.label")
                    }
                    step={0.01}
                    min={MIN_UNHCR_SERVICES_VALUE}
                  />
                </Row>
              )}

              <Label>{t("auctionForm.focalPoint")}</Label>
              <Row>
                <TextField
                  name="focalPoint.fullName"
                  placeholder={t("auctionForm.fullName")}
                />
                <TextField
                  name="focalPoint.email"
                  placeholder={t("auctionForm.email")}
                />
              </Row>
              <Row>
                <TextField
                  name="focalPoint.phone"
                  placeholder={t("auctionForm.phone")}
                />
              </Row>

              <Label>{t("auctionForm.representative")}</Label>
              <Row>
                <TextField
                  name="representative.fullName"
                  placeholder={t("auctionForm.fullName")}
                />
                <TextField
                  name="representative.email"
                  placeholder={t("auctionForm.email")}
                />
              </Row>
              <Row>
                <TextField
                  name="representative.phone"
                  placeholder={t("auctionForm.phone")}
                />
              </Row>
              {values.isOnline && (
                <Row>
                  <FileField
                    name="auctionImage"
                    accept="image/*"
                    label={t("lotForm.photo.label")}
                    placeholder={t("lotForm.photo.placeholder")}
                    onChange={(event) => {
                      handlePhotoField(
                        event.currentTarget.files,
                        setFieldValue
                      );
                    }}
                  />
                </Row>
              )}
              {values.isOnline && auctionImage && (
                <ImageContainer>
                  <ImageDeleteButton
                    onClick={() => {
                      setFieldValue("auctionImage", null);
                      setAuctionImage("");
                      setUploadedPhoto("");
                    }}
                  >
                    x
                  </ImageDeleteButton>
                  <img src={auctionImage} alt="auction logo" />
                </ImageContainer>
              )}
              {values.isOnline && requestTopics && requestTopics.length > 0 && (
                <>
                  <Label style={{ margin: "16px 0" }}>
                    {t("auctionForm.communicationFocalPoints")}
                  </Label>

                  {/* Column Headers - Desktop Only */}
                  <DesktopLayout>
                    <Row style={{ width: "100%" }}>
                      <DesktopTopicColumn></DesktopTopicColumn>
                      <DesktopColumnHeader>
                        <TopicNameLabel>
                          {t("auctionForm.bccGasTeamRepresentative")}
                        </TopicNameLabel>
                      </DesktopColumnHeader>
                      <DesktopColumnHeader>
                        <TopicNameLabel>
                          {t("auctionForm.bccCoFocalPoint")}
                        </TopicNameLabel>
                      </DesktopColumnHeader>
                      <DesktopEmailColumnHeader>
                        <TopicNameLabel>
                          {t("auctionForm.customEmails")}
                        </TopicNameLabel>
                      </DesktopEmailColumnHeader>
                    </Row>
                  </DesktopLayout>

                  {/* Topic Rows */}
                  {requestTopics.map((topic) => (
                    <TopicContainer key={topic.id}>
                      {/* Desktop Layout */}
                      <DesktopLayout>
                        <Row
                          style={{
                            marginBottom: "0",
                            alignItems: "flex-start",
                            width: "100%",
                          }}
                        >
                          <DesktopTopicColumn>
                            <TopicNameLabel>{topic.name}</TopicNameLabel>
                          </DesktopTopicColumn>
                          <DesktopSwitchContainer>
                            <SwitchField
                              name={`requestTopics.${topic.id}.bcc_gas_representative`}
                              center={true}
                              onChange={(checked) => {
                                setFieldValue(
                                  `requestTopics.${topic.id}.topic`,
                                  topic.id
                                );
                                setFieldValue(
                                  `requestTopics.${topic.id}.bcc_gas_representative`,
                                  checked
                                );
                              }}
                            />
                          </DesktopSwitchContainer>
                          <DesktopSwitchContainer>
                            <SwitchField
                              name={`requestTopics.${topic.id}.bcc_co_focal_point`}
                              center={true}
                              onChange={(checked) => {
                                setFieldValue(
                                  `requestTopics.${topic.id}.topic`,
                                  topic.id
                                );
                                setFieldValue(
                                  `requestTopics.${topic.id}.bcc_co_focal_point`,
                                  checked
                                );
                              }}
                            />
                          </DesktopSwitchContainer>
                          <DesktopEmailColumn>
                            <EmailField
                              topicId={topic.id}
                              topicName={topic.name}
                            />
                          </DesktopEmailColumn>
                        </Row>
                      </DesktopLayout>

                      {/* Mobile Layout */}
                      <MobileLayout>
                        <TopicNameLabel bold>{topic.name}</TopicNameLabel>

                        {/* Neat Switch Layout for Mobile */}
                        <MobileSwitchField
                          topicId={topic.id}
                          label={t("auctionForm.bccGasTeamRepresentative")}
                          fieldName="bcc_gas_representative"
                          setFieldValue={setFieldValue}
                        />

                        <MobileSwitchField
                          topicId={topic.id}
                          label={t("auctionForm.bccCoFocalPoint")}
                          fieldName="bcc_co_focal_point"
                          setFieldValue={setFieldValue}
                        />

                        {/* Custom Emails Section for Mobile */}
                        <CustomEmailsContainer>
                          <CustomEmailsLabel>
                            {t("auctionForm.customEmails")}
                          </CustomEmailsLabel>
                          <EmailFieldWrapper>
                            <EmailField
                              topicId={topic.id}
                              topicName={topic.name}
                            />
                          </EmailFieldWrapper>
                        </CustomEmailsContainer>
                      </MobileLayout>
                    </TopicContainer>
                  ))}
                </>
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
